// ─── Mid-table rebuy rules (cash games: private/public) ──────────────
// The one definition of what a game's `maxRebuys` setting means, shared by the server and,
// later, the app's create screens and rebuy prompt so the two can never disagree (PP-4881).
// Tournaments are separate: they use `tournament.config.maxRebuys` (re-entry is coming).
//
//   0         rebuys off ("None" in the create screen)
//   1, 2, 3…  at most that many rebuys per player
//   not set   unlimited. Any create path that omits `maxRebuys` gets this. getGameState sends
//             it to the app as `null`.
//
// The app uses it only in the in-game settings panel so far. The create screen still has its own
// [1, 2, 3] list (`RebuyCountPicker.tsx`), kept in step with MID_TABLE_REBUY_COUNT_OPTIONS by hand.

/** Stored value for "None" (rebuys off). Must be sent explicitly: an omitted value means unlimited. */
export const MID_TABLE_REBUYS_NONE = 0

/** Rebuy counts the create screen offers, besides "None". */
export const MID_TABLE_REBUY_COUNT_OPTIONS: readonly number[] = [1, 2, 3]

/** True when a player who has used `rebuysUsed` rebuys may take another under `maxRebuys`. */
export function canRebuy(maxRebuys: number | null | undefined, rebuysUsed: number | undefined): boolean {
  if (maxRebuys == null) return true
  return (rebuysUsed ?? 0) < maxRebuys
}

export type MaxRebuysDescription = { kind: 'none' } | { kind: 'limit'; count: number } | { kind: 'unlimited' }

/** What a game's rebuy setting is, for display: "None", the count, or "Unlimited". */
export function describeMaxRebuys(maxRebuys: number | null | undefined): MaxRebuysDescription {
  if (maxRebuys == null) return { kind: 'unlimited' }
  if (maxRebuys === MID_TABLE_REBUYS_NONE) return { kind: 'none' }
  return { kind: 'limit', count: maxRebuys }
}
